/**
 * TenantFulfillmentModal Component
 *
 * Modal for guiding global admins through the manual tenant creation process.
 * Displays:
 * - Request details (organization name, requested name)
 * - Checklist of manual steps
 * - Link to Azure portal for tenant creation
 * - Input fields for tenant ID and domain
 * - Complete button to finish fulfillment (enabled when all fields complete)
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Checkbox,
  Input,
  Label,
  Link,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../common/AsyncButton';
import { Dismiss24Regular, Open24Regular } from '@fluentui/react-icons';
import { Alert } from '../common/Alert';
import type { TenantRequest, TenantRegistrationRequest, FulfillmentChecklist } from '../../types/tenantRequest';

const useStyles = makeStyles({
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  sectionWithTopMargin: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    marginTop: tokens.spacingVerticalL,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
  },
  requestInfo: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  infoRow: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  infoLabel: {
    color: tokens.colorNeutralForeground3,
    minWidth: '120px',
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  azureLink: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
  },
});

export interface TenantFulfillmentModalProps {
  isOpen: boolean;
  request: TenantRequest;
  onClose: () => void;
  onSave: (checklist: FulfillmentChecklist) => void | Promise<void>;
  onComplete: (tenantData: TenantRegistrationRequest) => void | Promise<void>;
}

export function TenantFulfillmentModal({
  isOpen,
  request,
  onClose,
  onSave,
  onComplete,
}: TenantFulfillmentModalProps) {
  const styles = useStyles();

  // Initialize checklist state from request
  const [checklist, setChecklist] = useState<FulfillmentChecklist>({
    create_tenant: false,
    create_admin_account: false,
    login_as_admin: false,
    create_edge_profile: false,
    add_payment_method: false,
    update_organization_profile: false,
    add_trial_licenses: false,
    assign_licenses: false,
    disable_security_defaults: false,
    configure_inbound_cross_tenant_access: false,
    configure_outbound_cross_tenant_access: false,
    add_guest_admin: false,
    grant_exchange_admin_role: false,
    domain_entered: '',
  });

  // Form state for tenant data
  const [domain, setDomain] = useState('');

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    domain?: string;
  }>({});

  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens or request changes
  useEffect(() => {
    if (isOpen && request) {
      const existingChecklist = request.fulfillment_checklist;
      setChecklist({
        create_tenant: existingChecklist?.create_tenant ?? false,
        create_admin_account: existingChecklist?.create_admin_account ?? false,
        login_as_admin: existingChecklist?.login_as_admin ?? false,
        create_edge_profile: existingChecklist?.create_edge_profile ?? false,
        add_payment_method: existingChecklist?.add_payment_method ?? false,
        update_organization_profile: existingChecklist?.update_organization_profile ?? false,
        add_trial_licenses: existingChecklist?.add_trial_licenses ?? false,
        assign_licenses: existingChecklist?.assign_licenses ?? false,
        disable_security_defaults: existingChecklist?.disable_security_defaults ?? false,
        configure_inbound_cross_tenant_access: existingChecklist?.configure_inbound_cross_tenant_access ?? false,
        configure_outbound_cross_tenant_access: existingChecklist?.configure_outbound_cross_tenant_access ?? false,
        add_guest_admin: existingChecklist?.add_guest_admin ?? false,
        grant_exchange_admin_role: existingChecklist?.grant_exchange_admin_role ?? false,
        domain_entered: existingChecklist?.domain_entered ?? '',
      });
      setDomain(existingChecklist?.domain_entered ?? '');
      setValidationErrors({});
      setIsSaving(false);
      setIsSubmitting(false);
    }
  }, [isOpen, request]);

  const handleChecklistChange = (key: keyof FulfillmentChecklist, value: boolean) => {
    setChecklist(prev => ({ ...prev, [key]: value }));
  };

  const handleDomainChange = (value: string) => {
    setDomain(value);
    setChecklist(prev => ({ ...prev, domain_entered: value }));
    if (validationErrors.domain) {
      setValidationErrors(prev => ({ ...prev, domain: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!domain.trim()) {
      errors.domain = 'Domain is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(checklist);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleRelease = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onComplete({
        tenant_name: request.requested_name,
        domain: domain.trim(),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setValidationErrors({});
    setIsSaving(false);
    setIsSubmitting(false);
    onClose();
  };

  // Validate domain format (e.g., contoso.onmicrosoft.com)
  const isValidDomain = (value: string): boolean => {
    const domainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;
    return domainRegex.test(value.trim());
  };

  // Check if all required fields are complete and valid
  const isFormComplete =
    checklist.create_tenant &&
    checklist.create_admin_account &&
    checklist.login_as_admin &&
    checklist.create_edge_profile &&
    checklist.add_payment_method &&
    checklist.update_organization_profile &&
    checklist.add_trial_licenses &&
    checklist.assign_licenses &&
    checklist.disable_security_defaults &&
    checklist.configure_inbound_cross_tenant_access &&
    checklist.configure_outbound_cross_tenant_access &&
    checklist.add_guest_admin &&
    isValidDomain(domain);

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface data-testid="tenant-fulfillment-modal" style={{ maxWidth: '600px' }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
                disabled={isSaving || isSubmitting}
                aria-label="Close"
                data-testid="fulfillment-close-button"
              />
            }
          >
            Tenant Fulfillment
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {/* Request Information */}
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>Request Details</Text>
              <div className={styles.requestInfo}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Organization:</Text>
                  <Text>{request.organization_name}</Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Requested Name:</Text>
                  <Text>{request.requested_name}</Text>
                </div>
              </div>
            </div>

            {/* Azure Portal Link - Opens Manage Tenants where tenants can be created */}
            <div className={styles.sectionWithTopMargin}>
              <Link
                href="https://portal.azure.com/#create/Microsoft.AzureActiveDirectory"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.azureLink}
              >
                <Open24Regular />
                Open Azure Portal
              </Link>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Opens the Manage Tenants page in the Azure portal where new tenants can be created
              </Text>
            </div>

            {/* Checklist */}
            <div className={styles.sectionWithTopMargin}>
              <Text className={styles.sectionTitle}>Fulfillment Checklist</Text>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="create-tenant-checkbox"
                  label="Create tenant"
                  checked={checklist.create_tenant ?? false}
                  onChange={(_, data) => handleChecklistChange('create_tenant', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="create-admin-account-checkbox"
                  label="Create admin account"
                  checked={checklist.create_admin_account ?? false}
                  onChange={(_, data) => handleChecklistChange('create_admin_account', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="login-as-admin-checkbox"
                  label="Login as admin account"
                  checked={checklist.login_as_admin ?? false}
                  onChange={(_, data) => handleChecklistChange('login_as_admin', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="create-edge-profile-checkbox"
                  label="Create profile in Edge for admin account"
                  checked={checklist.create_edge_profile ?? false}
                  onChange={(_, data) => handleChecklistChange('create_edge_profile', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="add-payment-method-checkbox"
                  label="Add payment method"
                  checked={checklist.add_payment_method ?? false}
                  onChange={(_, data) => handleChecklistChange('add_payment_method', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="update-organization-profile-checkbox"
                  label="Update organization profile"
                  checked={checklist.update_organization_profile ?? false}
                  onChange={(_, data) => handleChecklistChange('update_organization_profile', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="add-trial-licenses-checkbox"
                  label="Add trial licenses (Microsoft Business Basic, Entra ID P2, Intune P1)"
                  checked={checklist.add_trial_licenses ?? false}
                  onChange={(_, data) => handleChecklistChange('add_trial_licenses', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="assign-licenses-checkbox"
                  label="Assign licenses to admin account"
                  checked={checklist.assign_licenses ?? false}
                  onChange={(_, data) => handleChecklistChange('assign_licenses', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="disable-security-defaults-checkbox"
                  label="Entra ID → Security → Properties: disable security defaults, enable access management for Azure resources"
                  checked={checklist.disable_security_defaults ?? false}
                  onChange={(_, data) => handleChecklistChange('disable_security_defaults', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="configure-inbound-cross-tenant-checkbox"
                  label="Managed tenant: Entra ID → External Identities → Cross Tenant Access Settings → add home tenant, configure inbound trust for automatic redemption"
                  checked={checklist.configure_inbound_cross_tenant_access ?? false}
                  onChange={(_, data) => handleChecklistChange('configure_inbound_cross_tenant_access', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="configure-outbound-cross-tenant-checkbox"
                  label="Home tenant: Entra ID → External Identities → Cross Tenant Access → add managed tenant, configure outbound trust for automatic redemption"
                  checked={checklist.configure_outbound_cross_tenant_access ?? false}
                  onChange={(_, data) => handleChecklistChange('configure_outbound_cross_tenant_access', data.checked === true)}
                />
              </div>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="add-guest-admin-checkbox"
                  label="Add irpilotadmin as guest account in managed tenant, grant global admin role"
                  checked={checklist.add_guest_admin ?? false}
                  onChange={(_, data) => handleChecklistChange('add_guest_admin', data.checked === true)}
                />
              </div>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalS }}>
                After onboarding in IRPilot:
              </Text>
              <div className={styles.checklistItem}>
                <Checkbox
                  id="grant-exchange-admin-checkbox"
                  label="Grant the enterprise service app the exchange admin role in the managed tenant"
                  checked={checklist.grant_exchange_admin_role ?? false}
                  onChange={(_, data) => handleChecklistChange('grant_exchange_admin_role', data.checked === true)}
                />
              </div>
            </div>

            {/* Tenant Information Fields */}
            <div className={styles.sectionWithTopMargin}>
              <Text className={styles.sectionTitle}>Tenant Information</Text>
              <div className={styles.formField}>
                <Label htmlFor="domain-input" required>
                  Domain
                </Label>
                <Input
                  id="domain-input"
                  value={domain}
                  onChange={(_, data) => handleDomainChange(data.value)}
                  placeholder="e.g., contoso.onmicrosoft.com"
                />
                {validationErrors.domain && (
                  <Alert intent="error">
                    <Text>{validationErrors.domain}</Text>
                  </Alert>
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions style={{ marginTop: tokens.spacingVerticalL }}>
            <AsyncButton
              appearance="secondary"
              onClick={handleSave}
              disabled={isSaving || isSubmitting}
              loading={isSaving}
              loadingText="Saving..."
              data-testid="fulfillment-save-button"
            >
              Save
            </AsyncButton>
            <AsyncButton
              appearance="primary"
              onClick={handleRelease}
              disabled={isSaving || isSubmitting || !isFormComplete}
              loading={isSubmitting}
              loadingText="Creating..."
              data-testid="fulfillment-complete-button"
            >
              Create Managed Tenant
            </AsyncButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
